
import React, { useState, useMemo } from 'react';
import { 
  Search, Plus, FileText, X, Save, Loader2, 
  DollarSign, Hash, 
  Trash2, Building2, ShoppingCart, FileCheck, Eye, Printer,
  FileCode, FileSpreadsheet
} from 'lucide-react';
import { Client, User, UserRole, Company } from './types';
import { Card, Badge } from './SharedComponents';

// Interface estendida para suportar o formulário completo
interface DetailedInvoiceItem {
  id: string;
  name: string;
  description: string;
  qty: number;
  rate: number;
  tax: number;
  amount: number;
}

export const InvoicesPage = ({ clients, currentUser, company, invoices, onUpdateInvoices, addNotification }: { clients: Client[], currentUser: User, company?: Company, invoices: any[], onUpdateInvoices: (i: any[]) => void, isNfseEnabled?: boolean, addNotification: any }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState<any | null>(null);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'ALL' | 'A_FATURAR'>('ALL');
  
  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      const matchesSearch = inv.number.toLowerCase().includes(search.toLowerCase()) || 
                          inv.clientName.toLowerCase().includes(search.toLowerCase());
      const matchesTab = activeTab === 'ALL' || (activeTab === 'A_FATURAR' && inv.status === 'A_FATURAR');
      return matchesSearch && matchesTab;
    });
  }, [invoices, search, activeTab]);

  const handleSaveInvoice = (newInvoice: any) => {
    onUpdateInvoices([newInvoice, ...invoices]);
    setIsModalOpen(false);
    addNotification("Sucesso", "Fatura criada com sucesso!", "success");
  };

  const handleFinalBilling = (id: string) => {
    onUpdateInvoices(invoices.map(inv => 
      inv.id === id ? { ...inv, status: 'FATURADO' } : inv
    ));
    addNotification("Faturamento", "Sistema Atualizado: Documento salvo e marcado como 'FATURADO'.", "success");
  };

  const isFinance = currentUser.role === UserRole.FINANCEIRO || currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.SUPER_ADMIN;
  const isCommercial = currentUser.role === UserRole.COMERCIAL || currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.SUPER_ADMIN;

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20 p-4 md:p-8 lg:p-10 text-secondary">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div className="space-y-1">
          <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tighter italic leading-none">Gestão de Faturamento</h1>
          <p className="text-gray-400 text-xs md:text-sm font-medium italic">Fila de processamento financeiro e controle de emissões.</p>
        </div>
        
        {isCommercial && (
          <button 
            onClick={() => setIsModalOpen(true)}
            className="bg-primary text-white px-10 py-5 rounded-[22px] font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-3 hover:brightness-110 shadow-xl shadow-primary/20 transition-all active:scale-95"
          >
            <Plus size={20} strokeWidth={3} /> Criar Nova Fatura
          </button>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white p-4 rounded-[28px] border border-gray-100 shadow-sm">
        <div className="flex bg-gray-50 p-1.5 rounded-2xl gap-1 w-full md:w-auto">
           <button onClick={() => setActiveTab('ALL')} className={`flex-1 md:flex-none px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'ALL' ? 'bg-white text-secondary shadow-md' : 'text-gray-400 hover:text-secondary'}`}>Todas</button>
           <button onClick={() => setActiveTab('A_FATURAR')} className={`flex-1 md:flex-none px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'A_FATURAR' ? 'bg-white text-blue-600 shadow-md' : 'text-gray-400 hover:text-secondary'}`}>A Faturar (Aprovadas)</button>
        </div>
        
        <div className="relative w-full md:w-80 group">
           <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-primary transition-colors" size={18} />
           <input 
            placeholder="Nº da Fatura ou Cliente..." 
            className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-transparent rounded-2xl outline-none font-bold text-sm focus:bg-white focus:ring-4 focus:ring-primary/5 transition-all"
            value={search}
            onChange={e => setSearch(e.target.value)}
           />
        </div>
      </div>

      <Card className="overflow-hidden border-none shadow-xl bg-white rounded-[40px]">
        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-gray-50/50 text-gray-400 text-[9px] uppercase font-black tracking-[0.2em] border-b border-gray-100">
                <th className="px-8 py-6">ID Documento</th>
                <th className="px-8 py-6">Parceiro / Cliente</th>
                <th className="px-8 py-6">Vencimento</th>
                <th className="px-8 py-6 text-right">Valor Total</th>
                <th className="px-8 py-6 text-center">Status</th>
                <th className="px-8 py-6 text-right">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredInvoices.length > 0 ? filteredInvoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-primary/[0.02] transition-colors group">
                  <td className="px-8 py-7 font-black text-secondary uppercase text-sm">
                    {inv.number}
                  </td>
                  <td className="px-8 py-7">
                    <p className="font-bold text-secondary uppercase text-xs">{inv.clientName}</p>
                    <p className="text-[9px] text-gray-400 font-bold mt-1 uppercase">ID: {inv.clientId}</p>
                  </td>
                  <td className="px-8 py-7">
                    <span className="text-[11px] font-black text-slate-500 uppercase tracking-tight">{inv.dueDate.split('-').reverse().join('/')}</span>
                  </td>
                  <td className="px-8 py-7 text-right font-black text-secondary text-base">
                    R$ {inv.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-8 py-7 text-center">
                    <Badge className={
                      inv.status === 'AGUARDANDO_FINANCEIRO' ? 'bg-amber-50 text-amber-600 border-amber-100' :
                      inv.status === 'A_FATURAR' ? 'bg-blue-50 text-blue-600 border-blue-100 shadow-sm ring-2 ring-blue-50' :
                      'bg-green-50 text-green-600 border-green-100'
                    }>
                      {inv.status === 'A_FATURAR' ? 'A FATURAR' : inv.status.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="px-8 py-7 text-right">
                    <button 
                      onClick={() => setViewingInvoice(inv)}
                      className="p-3 text-gray-300 hover:text-primary hover:bg-primary/5 rounded-xl transition-all"
                    >
                      <Eye size={20} />
                    </button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={6} className="px-8 py-32 text-center text-gray-200 font-black uppercase tracking-[0.3em] text-xs italic">Sem faturas no radar</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {isModalOpen && (
        <InvoiceFormModal 
          clients={clients} 
          onClose={() => setIsModalOpen(false)} 
          onSave={handleSaveInvoice} 
          addNotification={addNotification}
        />
      )}

      {/* PAINEL DE VISUALIZAÇÃO E AÇÃO FINANCEIRA */}
      {viewingInvoice && (
        <InvoiceDetailsPanel 
          invoice={viewingInvoice} 
          client={clients.find(c => c.id === viewingInvoice.clientId)}
          company={company}
          onClose={() => setViewingInvoice(null)} 
          onSaveAndBill={() => { handleFinalBilling(viewingInvoice.id); setViewingInvoice(null); }}
          isFinance={isFinance}
        />
      )}
    </div>
  );
};

const InvoiceDetailsPanel = ({ invoice, client, company, onClose, onSaveAndBill, isFinance }: any) => {
  const [isExporting, setIsExporting] = useState<string | null>(null);

  // Template HTML unificado, Moderno e Minimalista (Ajustado conforme feedback)
  const getSintetizzaTemplate = () => {
    // Logo da empresa puxado do perfil do gestor com fallback seguro
    const companyLogo = company?.logo || "https://i.postimg.cc/mD8V1G1w/eventlog-logo.png";

    return `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap');
        * { box-sizing: border-box; }
        body { font-family: 'Inter', Arial, sans-serif; font-size: 8.5pt; color: #1e293b; margin: 0; padding: 0; line-height: 1.3; background: white; }
        .wrapper { width: 100%; padding: 0; }
        .table-main { width: 100%; border-collapse: collapse; margin-bottom: 0px; table-layout: fixed; }
        .table-main td, .table-main th { border: 0.5pt solid #cbd5e1; padding: 6px; vertical-align: top; }
        .bg-muted { background-color: #f8fafc; font-weight: 700; text-transform: uppercase; font-size: 7.5pt; color: #64748b; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .logo-box { width: 25%; text-align: left; vertical-align: middle !important; border-right: none !important; }
        .header-box { width: 50%; text-align: center; vertical-align: middle !important; border-left: none !important; border-right: none !important; }
        .fatura-info { width: 25%; padding: 0 !important; border-left: none !important; }
        .title-main { font-size: 11pt; font-weight: 900; margin: 0; text-transform: uppercase; color: #0f172a; }
        .label-small { font-size: 6.5pt; color: #94a3b8; display: block; margin-bottom: 1px; font-weight: 900; text-transform: uppercase; }
        .value-bold { font-weight: 700; font-size: 8.5pt; text-transform: uppercase; display: block; color: #334155; }
        .logo-img { max-height: 50px; max-width: 140px; object-fit: contain; }
        .footer-line { border-top: 0.5pt solid #e2e8f0; margin-top: 15px; padding-top: 5px; font-size: 6.5pt; text-align: center; font-weight: 700; color: #94a3b8; text-transform: uppercase; }
        .total-box { background-color: #0f172a; color: white; padding: 8px !important; }
        .total-box .value-bold { color: white; font-size: 9.5pt; }
        .grid-2 { display: flex; width: 100%; }
        .grid-2 > div { flex: 1; border: 0.5pt solid #cbd5e1; padding: 6px; }
      </style>

      <div class="wrapper">
        <table class="table-main">
          <tr>
            <td class="logo-box">
               <img src="${companyLogo}" class="logo-img" alt="Logo" onerror="this.src='https://i.postimg.cc/mD8V1G1w/eventlog-logo.png'" />
            </td>
            <td class="header-box">
               <p class="title-main">${company?.name || 'Sintetizza Sistemas Logísticos'}</p>
               <p style="font-size: 6.5pt; margin-top: 3px; font-weight: 700; color: #64748b; letter-spacing: 1px;">LOCAÇÃO DE ESTRUTURAS • LOGÍSTICA • EVENTOS</p>
            </td>
            <td class="fatura-info">
               <div class="bg-muted text-center" style="padding: 4px; border-bottom: 0.5pt solid #cbd5e1;">DOCUMENTO DE FATURA</div>
               <div class="text-center" style="padding: 8px; font-size: 13pt; font-weight: 900; color: #00a8e0;">Nº ${invoice.number.replace('INV-', '')}</div>
               <div class="label-small text-center" style="border-top: 0.5pt solid #cbd5e1; padding-top: 3px;">DATA EMISSÃO: ${new Date(invoice.date).toLocaleDateString('pt-BR')}</div>
            </td>
          </tr>
        </table>

        <table class="table-main" style="margin-top: -0.5pt;">
          <tr>
            <td colspan="4" class="bg-muted">DADOS DO LOCATÁRIO (CLIENTE)</td>
          </tr>
          <tr>
            <td colspan="2"><span class="label-small">CLIENTE / PARCEIRO:</span><span class="value-bold">${invoice.clientName}</span></td>
            <td width="25%"><span class="label-small">CPF/CNPJ:</span><span class="value-bold">${client?.cnpj_cpf || '---'}</span></td>
            <td width="25%"><span class="label-small">CONTATO:</span><span class="value-bold">${client?.telefonePrincipal || '---'}</span></td>
          </tr>
          <tr>
            <td colspan="4"><span class="label-small">LOCAL DE ENTREGA E MONTAGEM:</span><span class="value-bold">${client?.enderecoCompleto || '---'}</span></td>
          </tr>
        </table>

        <table class="table-main" style="margin-top: -0.5pt;">
          <tr>
            <td colspan="4" class="bg-muted">DADOS DA LOCADORA (EMISSOR)</td>
          </tr>
          <tr>
            <td colspan="2"><span class="label-small">RAZÃO SOCIAL:</span><span class="value-bold">${company?.name || 'Sintetizza Sistemas'}</span></td>
            <td width="25%"><span class="label-small">CNPJ:</span><span class="value-bold">${company?.cnpj || '---'}</span></td>
            <td width="25%"><span class="label-small">CENTRAL:</span><span class="value-bold">${company?.whatsapp || '---'}</span></td>
          </tr>
          <tr>
            <td colspan="4"><span class="label-small">ENDEREÇO ADMINISTRATIVO:</span><span class="value-bold">${company?.address || '---'} • ${company?.neighborhood || ''} • ${company?.city || ''}/${company?.state || ''}</span></td>
          </tr>
        </table>

        <table class="table-main" style="margin-top: -0.5pt;">
          <tr>
            <td width="50%"><span class="label-small">REFERÊNCIA DE PROJETO / O.S.:</span><span class="value-bold">${invoice.number} - SERVIÇOS LOGÍSTICOS</span></td>
            <td width="50%"><span class="label-small">GESTOR RESPONSÁVEL:</span><span class="value-bold">${company?.managerName || 'ADMINISTRAÇÃO'}</span></td>
          </tr>
          <tr>
            <td><span class="label-small">PERÍODO DE UTILIZAÇÃO:</span><span class="value-bold">${new Date(invoice.date).toLocaleDateString('pt-BR')} — ${new Date(invoice.dueDate).toLocaleDateString('pt-BR')}</span></td>
            <td><span class="label-small">DADOS BANCÁRIOS (Sugerido):</span><span class="value-bold">BANCO SICREDI (748) • AG: 0723 • CC: 84691-5</span></td>
          </tr>
        </table>

        <table class="table-main" style="margin-top: -0.5pt;">
          <tr>
            <td width="50%" class="bg-muted">VALOR TOTAL E VENCIMENTO</td>
            <td width="50%" class="bg-muted">INSTRUÇÕES DE PAGAMENTO</td>
          </tr>
          <tr>
            <td style="padding: 10px;">
              <span class="label-small">DATA DE VENCIMENTO:</span>
              <span class="value-bold" style="font-size: 10.5pt; color: #ef4444; margin-bottom: 5px;">${new Date(invoice.dueDate).toLocaleDateString('pt-BR')}</span>
              <span class="label-small">VALOR LÍQUIDO DO DOCUMENTO:</span>
              <span class="value-bold" style="font-size: 10.5pt; color: #0f172a;">R$ ${invoice.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            </td>
            <td style="padding: 10px; font-size: 7.5pt; color: #64748b;">
              Pagamento via PIX (CNPJ) ou Boleto Bancário.<br/>
              Após o vencimento, multa de 2% e juros de 1% ao mês.<br/>
              Comprovante: <strong>${company?.contactEmail || 'financeiro@sintetizza.com.br'}</strong>
            </td>
          </tr>
        </table>

        <table class="table-main" style="margin-top: -0.5pt;">
          <thead>
            <tr class="bg-muted">
              <th width="12%" class="text-center">CÓDIGO</th>
              <th width="63%">DESCRIÇÃO DETALHADA DO PRODUTO / SERVIÇO</th>
              <th width="10%" class="text-center">QTD.</th>
              <th width="15%" class="text-right">SUBTOTAL</th>
            </tr>
          </thead>
          <tbody>
            ${invoice.items.map((it:any) => `
              <tr style="height: 28px;">
                <td class="text-center" style="vertical-align: middle; color: #94a3b8;">${Math.floor(100000 + Math.random() * 900000)}</td>
                <td class="value-bold" style="vertical-align: middle;">${it.name}</td>
                <td class="text-center value-bold" style="vertical-align: middle;">${it.qty}</td>
                <td class="text-right value-bold" style="vertical-align: middle;">R$ ${it.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
              </tr>
            `).join('')}
            <tr>
               <td colspan="3" class="text-right bg-muted" style="padding: 10px; font-size: 9pt; border-right: none;">TOTAL GERAL DO DOCUMENTO (R$):</td>
               <td class="text-right total-box" style="border-left: none;">
                  <span class="value-bold">R$ ${invoice.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
               </td>
            </tr>
          </tbody>
        </table>

        <table class="table-main" style="margin-top: -0.5pt;">
          <tr>
            <td class="bg-muted">OBSERVAÇÕES E RESSALVAS TÉCNICAS</td>
          </tr>
          <tr>
            <td style="height: 55px; font-style: italic; font-size: 7.5pt; padding: 8px; color: #64748b;">
              1. Documento referente à prestação de serviços de locação e montagem logística.<br/>
              2. O cliente declara ciência sobre as normas de devolução e conservação de materiais.<br/>
              3. Avarias detectadas no checkout de campo serão faturadas separadamente via Nota de Débito.<br/>
              4. Prazo para contestação deste faturamento: 24 horas úteis após o recebimento.
            </td>
          </tr>
        </table>

        <table class="table-main" style="margin-top: -0.5pt;">
          <tr>
            <td width="75%" class="bg-muted">PROTOCOLO DE RECEBIMENTO E CONFERÊNCIA</td>
            <td width="25%" class="bg-muted text-center">CONTROLE</td>
          </tr>
          <tr style="height: 65px;">
            <td>
               <table width="100%" border="0" style="border: none;">
                  <tr>
                     <td width="30%" style="border: none; padding: 10px;">
                        <span class="label-small">DATA DE RECEBIMENTO:</span>
                        <div style="margin-top: 10px; border-bottom: 0.5pt solid #cbd5e1; width: 100px;">&nbsp;</div>
                     </td>
                     <td width="70%" style="border: none; padding: 10px;">
                        <span class="label-small">ASSINATURA RESPONSÁVEL (CARIMBO/NOME):</span>
                        <div style="margin-top: 10px; border-bottom: 0.5pt solid #cbd5e1;">&nbsp;</div>
                     </td>
                  </tr>
               </table>
            </td>
            <td class="text-center" style="vertical-align: middle !important;">
               <span class="value-bold" style="font-size: 11pt; color: #00a8e0;">DOC: ${invoice.number.replace('INV-', '')}</span>
               <div style="margin-top: 2px; font-size: 6pt; font-weight: 700; color: #94a3b8;">ID: ${invoice.id}</div>
            </td>
          </tr>
        </table>
        
        <p class="footer-line">Documento Gerado Eletronicamente via EventLog PRO v4.5 — Emissão em ${new Date().toLocaleString('pt-BR')}</p>
      </div>
    `;
  };

  const exportExcel = () => {
    setIsExporting('excel');
    setTimeout(() => {
      const csvRows = [
        ["sep=;"],
        [`RELATORIO DE FATURAMENTO - ${company?.name || 'SINTETIZZA'}`],
        [`FATURA: ${invoice.number}`],
        [`CLIENTE: ${invoice.clientName}`],
        [`DATA EMISSAO: ${invoice.date}`],
        [`VENCIMENTO: ${invoice.dueDate}`],
        [""],
        ["ITEM", "QUANTIDADE", "VALOR UNITARIO (R$)", "VALOR TOTAL (R$)"],
        ...invoice.items.map((it: any) => [
          it.name,
          it.qty,
          it.rate.toFixed(2).replace('.', ','),
          it.amount.toFixed(2).replace('.', ',')
        ]),
        [""],
        ["", "", "TOTAL DA NOTA", invoice.total.toFixed(2).replace('.', ',')]
      ];

      const csvContent = csvRows.map(row => row.map((cell: any) => `"${cell}"`).join(";")).join("\r\n");
      const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Excel_Fatura_${invoice.number}.csv`;
      link.click();
      setIsExporting(null);
    }, 800);
  };

  const exportWord = () => {
    setIsExporting('word');
    setTimeout(() => {
      const content = `
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head><meta charset='utf-8'></head>
        <body>
          ${getSintetizzaTemplate()}
        </body>
        </html>
      `;
      const blob = new Blob([content], { type: 'application/msword' });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Fatura_${invoice.number}.doc`;
      link.click();
      setIsExporting(null);
    }, 800);
  };

  const exportPdf = () => {
    setIsExporting('pdf');
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Fatura ${invoice.number}</title>
          <style>
             @page { size: A4; margin: 4mm; }
             body { margin: 0; padding: 0; }
          </style>
        </head>
        <body>
          ${getSintetizzaTemplate()}
          <script>window.onload=function(){window.print();window.close();};</script>
        </body>
      </html>
    `);
    printWindow.document.close();
    setIsExporting(null);
  };

  return (
    <div className="fixed inset-0 bg-secondary/80 backdrop-blur-md z-[500] flex justify-end animate-in fade-in duration-300">
      <div className="bg-[#fcfdfe] h-full w-full max-w-2xl shadow-2xl flex flex-col animate-in slide-in-from-right duration-500 border-l border-gray-100">
         <div className="p-8 bg-slate-900 text-white shrink-0 flex justify-between items-start relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-[0.03] rotate-12 scale-150 pointer-events-none"><FileText size={200} /></div>
            <div className="relative z-10">
               <Badge className={
                  invoice.status === 'AGUARDANDO_FINANCEIRO' ? 'bg-amber-500 text-white border-none' : 
                  invoice.status === 'A_FATURAR' ? 'bg-blue-500 text-white border-none' :
                  'bg-emerald-500 text-white border-none'
               }>{invoice.status === 'A_FATURAR' ? 'A FATURAR' : invoice.status.replace('_', ' ')}</Badge>
               <h2 className="text-3xl font-black uppercase tracking-tight italic mt-4">{invoice.number}</h2>
               <p className="text-[10px] font-bold text-primary uppercase tracking-[0.4em] mt-2">Dossiê de Faturamento Clean</p>
            </div>
            <button onClick={onClose} className="p-3 bg-white/5 hover:bg-red-500/20 rounded-2xl transition-all active:scale-90"><X size={28} /></button>
         </div>

         <div className="flex-1 overflow-y-auto p-10 space-y-10 scrollbar-hide">
            <section className="space-y-6">
               <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] border-b pb-3 flex items-center gap-2 italic">
                  <Building2 size={16} /> Dados do Emissor
               </h3>
               <div className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm flex items-center gap-6">
                  <div className="w-16 h-16 rounded-[24px] bg-slate-50 flex items-center justify-center font-black text-2xl text-secondary shadow-inner overflow-hidden">
                    {company?.logo ? <img src={company.logo} className="w-full h-full object-contain" /> : company?.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-black text-secondary uppercase text-lg">{company?.name || 'Sintetizza Sistemas'}</p>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">CNPJ: {company?.cnpj || '---'}</p>
                  </div>
               </div>
            </section>

            <section className="space-y-4">
               <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] border-b pb-3 flex items-center gap-2 italic">
                  <ShoppingCart size={16} /> Itens da Fatura
               </h3>
               <div className="space-y-2">
                  {invoice.items?.map((it: any, idx: number) => (
                    <div key={idx} className="p-4 bg-white border border-gray-100 rounded-2xl flex justify-between items-center group hover:border-primary/20 transition-all">
                       <div>
                          <p className="text-xs font-black text-secondary uppercase leading-none">{it.name}</p>
                          <p className="text-[9px] text-gray-400 font-bold uppercase mt-1">{it.qty} UN x R$ {it.rate.toLocaleString('pt-BR')}</p>
                       </div>
                       <span className="font-black text-secondary">R$ {it.amount.toLocaleString('pt-BR')}</span>
                    </div>
                  ))}
               </div>
            </section>

            <section className="pt-6">
               <div className="bg-secondary p-8 rounded-[40px] text-white shadow-2xl relative overflow-hidden group border-4 border-white/5">
                  <div className="absolute top-0 right-0 p-4 opacity-5 rotate-12 group-hover:scale-110 transition-transform"><DollarSign size={100} /></div>
                  <div className="relative z-10">
                     <p className="text-[10px] font-black text-primary uppercase tracking-[0.4em] mb-2">Valor Líquido</p>
                     <p className="text-4xl font-black tracking-tighter leading-none">R$ {invoice.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  </div>
               </div>
            </section>
         </div>

         <div className="p-8 border-t border-gray-100 bg-white shrink-0 space-y-4">
            <div className="grid grid-cols-3 gap-3">
               <button 
                 disabled={isExporting !== null}
                 onClick={exportWord}
                 className="flex flex-col items-center gap-2 py-4 bg-blue-50 text-blue-600 rounded-2xl hover:bg-blue-600 hover:text-white transition-all group active:scale-95 disabled:opacity-50"
               >
                  {isExporting === 'word' ? <Loader2 size={20} className="animate-spin" /> : <FileCode size={20} />}
                  <span className="text-[8px] font-black uppercase tracking-widest">Word (.doc)</span>
               </button>
               <button 
                 disabled={isExporting !== null}
                 onClick={exportExcel}
                 className="flex flex-col items-center gap-2 py-4 bg-emerald-50 text-emerald-600 rounded-2xl hover:bg-emerald-600 hover:text-white transition-all group active:scale-95 disabled:opacity-50"
               >
                  {isExporting === 'excel' ? <Loader2 size={20} className="animate-spin" /> : <FileSpreadsheet size={20} />}
                  <span className="text-[8px] font-black uppercase tracking-widest">Excel (.csv)</span>
               </button>
               <button 
                 disabled={isExporting !== null}
                 onClick={exportPdf}
                 className="flex flex-col items-center gap-2 py-4 bg-rose-50 text-rose-600 rounded-2xl hover:bg-rose-600 hover:text-white transition-all group active:scale-95 disabled:opacity-50"
               >
                  {isExporting === 'pdf' ? <Loader2 size={20} className="animate-spin" /> : <Printer size={20} />}
                  <span className="text-[8px] font-black uppercase tracking-widest">PDF Minimalista</span>
               </button>
            </div>

            {isFinance && invoice.status === 'A_FATURAR' && (
              <button 
                onClick={onSaveAndBill}
                className="w-full py-5 bg-primary text-white rounded-[24px] font-black uppercase text-xs tracking-[0.2em] shadow-2xl shadow-primary/20 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-3"
              >
                <Save size={20} /> Salvar Alterações e Efetivar
              </button>
            )}
         </div>
      </div>
    </div>
  );
};

const InvoiceFormModal = ({ clients, onClose, onSave, addNotification }: any) => {
  const [formData, setFormData] = useState(() => ({
    clientId: '',
    number: `INV-${Math.floor(100000 + Math.random() * 900000)}`,
    date: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 2592000000).toISOString().split('T')[0],
    tags: [] as string[],
    paymentModes: ['Bank'],
    currency: 'BRL R$',
    saleAgent: 'Comercial Alpha',
    recurring: 'Não',
    discountType: 'Sem desconto',
    adminNote: '',
    discountPct: 0,
    adjustment: 0
  }));

  const [items, setItems] = useState<DetailedInvoiceItem[]>([
    { id: '1', name: '', description: '', qty: 1, rate: 0, tax: 0, amount: 0 }
  ]);

  const subTotal = useMemo(() => items.reduce((acc, item) => acc + item.amount, 0), [items]);
  const total = subTotal + Number(formData.adjustment);

  const addItem = () => {
    setItems([...items, { id: Date.now().toString(), name: '', description: '', qty: 1, rate: 0, tax: 0, amount: 0 }]);
  };

  const removeItem = (id: string) => {
    if (items.length > 1) setItems(items.filter(i => i.id !== id));
  };

  const updateItem = (id: string, field: keyof DetailedInvoiceItem, value: any) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        updated.amount = (Number(updated.qty) || 0) * (Number(updated.rate) || 0);
        return updated;
      }
      return item;
    }));
  };

  return (
    <div className="fixed inset-0 bg-secondary/90 backdrop-blur-md z-[500] flex items-center justify-center p-4">
      <div className="bg-white rounded-[40px] w-full max-w-6xl shadow-2xl overflow-hidden animate-in zoom-in-95 flex flex-col h-[90vh] border border-white/20">
        
        <div className="p-8 bg-[#1e293b] text-white flex justify-between items-center shrink-0">
           <div className="flex items-center gap-5">
              <div className="p-3 bg-primary rounded-2xl shadow-xl shadow-primary/20"><ShoppingCart size={32} /></div>
              <div>
                 <h2 className="text-2xl font-black uppercase tracking-tight italic leading-none">Novo Faturamento</h2>
                 <p className="text-[10px] font-bold text-primary uppercase tracking-[0.4em] mt-1">Interface Comercial de Geração</p>
              </div>
           </div>
           <button onClick={onClose} className="p-3 hover:bg-white/10 rounded-2xl transition-all active:scale-90"><X size={32} /></button>
        </div>

        <form className="flex-1 overflow-y-auto p-10 space-y-12 scrollbar-hide bg-[#fcfdfe]">
           <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="space-y-6">
                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4 flex items-center gap-2">
                       <Building2 size={12} className="text-primary" /> Cliente Destino *
                    </label>
                    <select required className="w-full bg-white border border-gray-100 p-5 rounded-[24px] outline-none font-bold text-secondary text-sm shadow-sm focus:ring-4 focus:ring-primary/5 transition-all appearance-none cursor-pointer" value={formData.clientId} onChange={e => setFormData({...formData, clientId: e.target.value})}>
                       <option value="">Selecione o Parceiro...</option>
                       {clients.map((c: any) => <option key={c.id} value={c.id}>{c.empresa}</option>)}
                    </select>
                 </div>

                 <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Data Emissão</label>
                       <input type="date" required className="w-full bg-white border border-gray-100 p-5 rounded-[24px] font-bold text-secondary shadow-sm outline-none focus:ring-4 focus:ring-primary/5" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} />
                    </div>
                    <div className="space-y-1.5">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Vencimento</label>
                       <input type="date" required className="w-full bg-white border border-gray-100 p-5 rounded-[24px] font-bold text-secondary shadow-sm outline-none focus:ring-4 focus:ring-primary/5" value={formData.dueDate} onChange={e => setFormData({...formData, dueDate: e.target.value})} />
                    </div>
                 </div>
              </div>

              <div className="space-y-6">
                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Nº Fatura (Automático)</label>
                    <div className="relative">
                       <Hash className="absolute left-5 top-1/2 -translate-y-1/2 text-primary" size={18} />
                       <input readOnly className="w-full bg-gray-50 border border-gray-100 p-5 pl-14 rounded-[24px] font-black text-secondary tracking-widest shadow-inner cursor-not-allowed" value={formData.number} />
                    </div>
                 </div>
                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Método Sugerido</label>
                    <select className="w-full bg-white border border-gray-100 p-5 rounded-[24px] font-bold text-secondary shadow-sm appearance-none cursor-pointer outline-none">
                       <option>PIX / Transferência</option>
                       <option>Boleto Bancário</option>
                       <option>Stripe Checkout</option>
                    </select>
                 </div>
              </div>
           </div>

           <div className="space-y-6 pt-12 border-t border-gray-100">
              <h3 className="text-sm font-black text-secondary uppercase tracking-widest flex items-center gap-3 italic">
                 <ShoppingCart size={18} className="text-primary" /> Itens de Locação / Serviço
              </h3>
              <div className="overflow-hidden border border-gray-100 rounded-[32px] bg-white shadow-sm">
                 <table className="w-full text-left border-collapse">
                    <thead className="bg-gray-50/80 font-black uppercase text-[9px] text-gray-400 tracking-widest border-b border-gray-100">
                       <tr>
                          <th className="p-5 w-[45%]">Descrição do Serviço</th>
                          <th className="p-5 text-center w-24">Qtd</th>
                          <th className="p-5 text-center w-32">R$ Unitário</th>
                          <th className="p-5 text-right pr-8">Total</th>
                          <th className="p-5 w-12 text-center"></th>
                       </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                       {items.map((item) => (
                         <tr key={item.id} className="hover:bg-primary/[0.01]">
                            <td className="p-4">
                               <input 
                                required
                                className="w-full bg-slate-50 border border-gray-100 p-3 rounded-xl outline-none font-bold text-secondary text-xs focus:bg-white focus:border-primary transition-all shadow-inner" 
                                placeholder="Título do material ou serviço..."
                                value={item.name}
                                onChange={e => updateItem(item.id, 'name', e.target.value)}
                               />
                            </td>
                            <td className="p-4 text-center">
                               <input type="number" className="w-16 bg-white border border-gray-100 p-3 rounded-xl text-center font-black text-secondary shadow-sm" value={item.qty} onChange={e => updateItem(item.id, 'qty', e.target.value)} />
                            </td>
                            <td className="p-4 text-center">
                               <input type="number" className="w-28 bg-white border border-gray-100 p-3 rounded-xl text-center font-black text-secondary shadow-sm" placeholder="0,00" value={item.rate || ''} onChange={e => updateItem(item.id, 'rate', e.target.value)} />
                            </td>
                            <td className="p-4 text-right pr-8">
                               <p className="font-black text-secondary text-sm">R$ {item.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                            </td>
                            <td className="p-4 text-center">
                               <button type="button" onClick={() => removeItem(item.id)} className="p-2 text-gray-200 hover:text-red-500 transition-all active:scale-90"><Trash2 size={16} /></button>
                            </td>
                         </tr>
                       ))}
                    </tbody>
                 </table>
                 <div className="p-6 bg-white border-t border-gray-50 flex justify-between items-center">
                    <button type="button" onClick={addItem} className="flex items-center gap-2 text-[10px] font-black text-primary uppercase tracking-widest hover:underline active:scale-95 transition-all">
                       <Plus size={16} strokeWidth={4} /> Adicionar Item à Linha
                    </button>
                    <span className="text-[9px] font-bold text-gray-300 uppercase tracking-widest">Saldo Sync Ativo</span>
                 </div>
              </div>
           </div>

           <div className="flex justify-end pt-12 pb-20">
              <div className="bg-secondary p-8 rounded-[40px] text-white flex flex-col gap-3 min-w-[350px] shadow-2xl relative overflow-hidden border-4 border-white/5">
                 <div className="absolute top-0 right-0 p-4 opacity-5 rotate-12"><DollarSign size={80} /></div>
                 <p className="text-[10px] font-black text-primary uppercase tracking-[0.3em] relative z-10 leading-none mb-1">Total da Nota de Débito</p>
                 <p className="text-4xl font-black tracking-tighter relative z-10">R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                 <div className="h-[1px] bg-white/10 my-1 relative z-10"></div>
                 <p className="text-[8px] font-bold text-white/30 uppercase italic relative z-10">Este valor passará por auditoria na Controladoria Master.</p>
              </div>
           </div>
        </form>

        <div className="p-8 border-t border-gray-100 bg-gray-50 flex justify-between items-center shrink-0">
           <button type="button" onClick={onClose} className="px-10 py-5 rounded-[22px] font-black uppercase text-[10px] text-gray-400 hover:text-secondary transition-all">Descartar</button>
           <button 
             type="button" 
             onClick={() => {
               if (!formData.clientId) return addNotification("Campo Obrigatório", "Selecione um cliente.", "warning");
               const client = clients.find((c:any) => c.id === formData.clientId);
               onSave({
                 ...formData,
                 id: `inv-${Date.now()}`,
                 clientName: client?.empresa || 'Cliente Avulso',
                 total,
                 items,
                 status: 'AGUARDANDO_FINANCEIRO'
               });
             }}
             className="bg-emerald-600 text-white px-14 py-5 rounded-[22px] font-black uppercase text-[11px] tracking-[0.2em] shadow-2xl shadow-emerald-100 hover:brightness-110 active:scale-95 transition-all flex items-center gap-3"
           >
             <FileCheck size={20} strokeWidth={2.5} /> Enviar para Auditoria
           </button>
        </div>
      </div>
    </div>
  );
};
